// Wraps the Esro (Tixly) browser SDK, see types/esro.d.ts. Fields the SDK
// passes through unchanged are `unknown`; only what this file computes or what
// the callers use is typed.

export interface EsroBasket {
  tickets: {
    id: unknown;
    eventId: unknown;
    hall: unknown;
    date: Date;
    name: unknown;
    total: unknown;
  }[];
  merchandise: {
    id: unknown;
    merchandiseId: string;
    name: unknown;
    total: number;
  }[];
  sumTotal: number;
}

/** What the `clientDetailsChanged` handler in effects/sroClient reads. */
export interface EsroClientDetails {
  id: string | number;
  name: { first: string; last: string };
  contactDetails?: { typeName: string; value: string }[];
}

export interface EsroApi {
  addEventListener(
    eventType: 'basketChanged',
    handler: (basket: EsroBasket) => void
  ): void;
  addEventListener(
    eventType: 'clientDetailsChanged',
    handler: (client: EsroClientDetails) => void
  ): void;
  removeEventListener(
    eventType: 'basketChanged',
    handler: (basket: EsroBasket) => void
  ): void;
  removeEventListener(
    eventType: 'clientDetailsChanged',
    handler: (client: EsroClientDetails) => void
  ): void;
  getEmptyCustomer(): Promise<unknown>;
  getCustomer(): Promise<{ Result: { Id: unknown } }>;
  signin(username: string, password: string): Promise<void>;
  signout(): Promise<void>;
}

import { debounce } from 'lodash';
import { useRef, useState, useEffect } from 'react';
import md5 from 'md5';

type BasketHandler = (basket: EsroBasket) => void;
type ClientHandler = (client: EsroClientDetails) => void;

let basketChanged: BasketHandler[] = [];
let clientDetailsChanged: ClientHandler[] = [];
let globalApi: EsroSdk | null = null;

window.dntEsroCustomerLogoutResult = () => {};
window.dntEsroCustomerLoginResult = () => {};
window.dntEsroEmptyCustomerResult = () => {};
window.dntEsroCustomerResult = () => {};

const refreshSessionState = debounce(() => {
  if (globalApi) {
    globalApi.refreshSessionState();
  }
}, 500);

function transformNok(nok: string) {
  const cleaned = nok
    .replace('Kr', '')
    .replace('kr', '')
    .replace(',', '.')
    .trim();
  return parseFloat(cleaned);
}

const initApi = () => {
  globalApi!.attachEventHandler('basketChanged', e => {
    let tickets: EsroBasket['tickets'] = [];
    let merchandise: EsroBasket['merchandise'] = [];
    if (e) {
      if (e.Tickets) {
        tickets = e.Tickets.map(ticket => {
          const timestamp = ticket.EventDateTime.replace('/Date(', '').replace(
            ')/',
            ''
          );
          const date = new Date(parseInt(timestamp));
          return {
            id: ticket.Id,
            eventId: ticket.EventId,
            hall: ticket.HallName,
            date,
            name: ticket.ShowName,
            total: ticket.TotalPriceAmount,
          };
        });
      }

      if (e.Merchandise) {
        merchandise = e.Merchandise.map(merch => {
          const id = md5(`${merch.Name}_${merch.TotalValue}`);
          return {
            id: merch.Id,
            merchandiseId: id,
            name: merch.Name,
            total: transformNok(merch.TotalValue),
          };
        });
      }
    }

    basketChanged.forEach(handler => {
      if (tickets.length || merchandise.length) {
        handler({ tickets, merchandise, sumTotal: transformNok(e.totalValue) });
      }
    });
  });

  globalApi!.attachEventHandler('clientDetailsChanged', e => {
    clientDetailsChanged.forEach(handler => {
      handler(e);
    });
  });
};

const addEventListener: EsroApi['addEventListener'] = (
  eventType: string,
  handler: BasketHandler | ClientHandler
) => {
  if (eventType == 'basketChanged') {
    basketChanged.push(handler as BasketHandler);
  }

  if (eventType == 'clientDetailsChanged') {
    clientDetailsChanged.push(handler as ClientHandler);
  }

  if (globalApi) {
    refreshSessionState();
  }
};

const removeEventListener: EsroApi['removeEventListener'] = (
  eventType: string,
  handler: BasketHandler | ClientHandler
) => {
  if (eventType == 'basketChanged') {
    basketChanged = basketChanged.filter(h => h !== handler);
  }

  if (eventType == 'clientDetailsChanged') {
    clientDetailsChanged = clientDetailsChanged.filter(h => h !== handler);
  }
};

const signout = () => {
  return new Promise<void>((resolve, reject) => {
    if (globalApi) {
      window.dntEsroCustomerLogoutResult = e => {
        if (e.HasError) {
          reject({
            message: e.ErrorDescription,
          });
        } else {
          resolve();
        }
      };
      globalApi.customerLogout('dntEsroCustomerLogoutResult');
    } else {
      reject({
        message: 'Api missing',
      });
    }
  });
};

const signin = (username: string, password: string) => {
  return new Promise<void>((resolve, reject) => {
    if (globalApi) {
      window.dntEsroCustomerLoginResult = e => {
        if (e.HasError) {
          reject({
            message: e.ErrorDescription,
          });
        } else {
          resolve();
        }
      };
      globalApi.customerLogin(username, password, 'dntEsroCustomerLoginResult');
    } else {
      reject({
        message: 'Api missing',
      });
    }
  });
};

const getEmptyCustomer = () => {
  return new Promise<unknown>((resolve, reject) => {
    window.dntEsroEmptyCustomerResult = e => {
      if (e.HasError) {
        reject({
          message: e.ErrorDescription,
        });
      } else {
        resolve(e.Result);
      }
    };
    globalApi!.getEmptyCustomer('dntEsroEmptyCustomerResult');
  });
};

const getCustomer = () => {
  return new Promise<{ Result: { Id: unknown } }>((resolve, reject) => {
    window.dntEsroCustomerResult = e => {
      if (e.HasError) {
        reject({
          message: e.ErrorDescription,
        });
      } else {
        resolve(e as { Result: { Id: unknown } });
      }
    };
    globalApi!.getCustomerDetails('dntEsroCustomerResult');
  });
};

export default () => {
  const api = useRef<EsroApi | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (typeof $esro === 'object') {
        clearInterval(interval);
        globalApi = $esro;

        api.current = {
          addEventListener,
          removeEventListener,
          getEmptyCustomer,
          getCustomer,
          signin,
          signout,
        };

        setReady(true);
      }
    }, 200);

    return function cleanup() {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (globalApi) {
      initApi();
      refreshSessionState();
    }
  }, [ready]);

  return api.current;
};
